import { useAuth } from '@/contexts/AuthContext'
import { useUserCompanies } from '@/features/companies'
import { db } from '@/lib/firebase'
import { notificationService } from '@/services/notificationService'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { collection, doc, getDocs, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { notificationKeys } from './query-keys'
import type { SimplifiedNotification } from './types'

/**
 * Company-Aware Notification Query Hooks
 * Following cursor rules patterns with proper stale times and error handling
 * Clean Code: Descriptive names, proper typing, consistent patterns
 * Company Context: Automatically includes user's company notifications
 */

export const useNotifications = (userId: string, platform: 'web' | 'mobile' = 'web') => {
  const { userData } = useAuth()
  const companyId = userData?.companyId

  return useQuery({
    queryKey: notificationKeys.list(userId, platform, companyId),
    queryFn: () => notificationService.getUserNotifications(userId, platform, companyId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

export const useUnreadCount = (userId: string, platform: 'web' | 'mobile' = 'web') => {
  const { userData } = useAuth()
  const companyId = userData?.companyId

  return useQuery({
    queryKey: notificationKeys.count(userId, platform, companyId),
    queryFn: () => notificationService.getUnreadCount(userId, platform, companyId),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useUnreadCountByType = (userId: string, platform: 'web' | 'mobile' = 'web') => {
  const { userData } = useAuth()
  const companyId = userData?.companyId

  return useQuery({
    queryKey: notificationKeys.countByType(userId, platform, companyId),
    queryFn: () => notificationService.getUnreadCountByType(userId, platform, companyId),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Complete notification counts including follow requests
 * Returns: { total, unread, followRequests }
 */
export const useNotificationCounts = (userId: string, companyId?: string, platform: 'web' | 'mobile' = 'web') => {
  const { userData } = useAuth()
  const currentCompanyId = companyId || userData?.companyId

  // Get unread notification count
  const { data: unreadCount = 0 } = useUnreadCount(userId, platform)

  // Get company data for request counts
  const { data: userCompaniesData } = useUserCompanies(userId, {
    enabled: !!userId,
  })

  return useQuery({
    queryKey: ['notificationCounts', userId, currentCompanyId, platform, unreadCount, userCompaniesData?.companies],
    queryFn: () => {
      const company = userCompaniesData?.companies?.find((c) => c.id === currentCompanyId)

      const followRequests = company?.followRequestsCount || 0
      const total = unreadCount + followRequests

      return {
        total,
        unread: unreadCount,
        followRequests,
      }
    },
    enabled: !!userId && !!userCompaniesData && unreadCount !== undefined,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * REALTIME notification counts including follow requests
 * Uses Firestore onSnapshot for instant updates
 * Returns: { total, unread, followRequests }
 */
export const useNotificationCountsRealtime = (
  userId: string,
  companyId?: string,
  platform: 'web' | 'mobile' = 'web',
) => {
  const { userData } = useAuth()
  const currentCompanyId = companyId || userData?.companyId
  const [counts, setCounts] = useState({ total: 0, unread: 0, followRequests: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!userId) {
      setIsLoading(false)
      setCounts({ total: 0, unread: 0, followRequests: 0 })
      return
    }

    setIsLoading(true)

    let unsubscribePersonal: (() => void) | null = null
    let unsubscribeCompany: (() => void) | null = null
    let unsubscribeCompanyNotifications: (() => void) | null = null

    let unreadCount = 0
    let requestCounts = { followRequests: 0 }
    let isPersonalLoaded = false
    let isCompanyLoaded = !currentCompanyId // If no company, consider loaded

    const updateTotalCounts = () => {
      // Only update if both personal and company data are loaded
      if (!isPersonalLoaded || !isCompanyLoaded) {
        return
      }

      const total = unreadCount + requestCounts.followRequests
      const newCounts = {
        total,
        unread: unreadCount,
        followRequests: requestCounts.followRequests,
      }

      // Debug log to track notification counts
      console.log('🔔 Notification counts updated:', {
        unreadCount,
        followRequests: requestCounts.followRequests,
        total,
      })

      setCounts(newCounts)
      setIsLoading(false)
      setError(null)
    }

    const audiences = platform === 'web' ? ['web', 'all'] : ['mobile', 'all']

    // Listen to personal notifications
    const personalQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('audience', 'in', audiences),
      where('read', '==', false),
    )

    unsubscribePersonal = onSnapshot(
      personalQuery,
      (snapshot) => {
        // Explicitly handle empty collection (when deleted or no notifications)
        const personalUnread = snapshot.empty ? 0 : snapshot.size

        // If we have company notifications subscription, need to get company count too
        if (currentCompanyId && unsubscribeCompanyNotifications) {
          // Re-get company notifications count
          const companyQuery = query(
            collection(db, 'notifications'),
            where('companyId', '==', currentCompanyId),
            where('audience', 'in', audiences),
            where('read', '==', false),
          )

          getDocs(companyQuery)
            .then((companySnapshot) => {
              const companyUnread = companySnapshot.empty ? 0 : companySnapshot.size
              unreadCount = personalUnread + companyUnread
              isPersonalLoaded = true

              updateTotalCounts()
            })
            .catch(() => {
              unreadCount = personalUnread
              isPersonalLoaded = true
              updateTotalCounts()
            })
        } else {
          // No company or company subscription is not ready yet
          unreadCount = personalUnread
          isPersonalLoaded = true
          updateTotalCounts()
        }
      },
      (err) => {
        // On error, reset to 0
        unreadCount = 0
        isPersonalLoaded = true
        updateTotalCounts()
        setError(err as Error)
        setIsLoading(false)
      },
    )

    // If user has a company, listen to company document for request counts and company notifications
    if (currentCompanyId) {
      // Listen to company document for request counts
      const companyDocRef = doc(db, 'companies', currentCompanyId)

      unsubscribeCompany = onSnapshot(
        companyDocRef,
        (companyDoc) => {
          try {
            if (companyDoc.exists()) {
              const companyData = companyDoc.data()
              const newRequestCounts = {
                followRequests: companyData?.followRequestsCount || 0,
              }

              requestCounts = newRequestCounts
              isCompanyLoaded = true
              updateTotalCounts()
            } else {
              requestCounts = { followRequests: 0 }
              isCompanyLoaded = true
              updateTotalCounts()
            }
          } catch (err) {
            setError(err as Error)
            setIsLoading(false)
          }
        },
        (err) => {
          setError(err as Error)
          setIsLoading(false)
        },
      )

      // Listen to company notifications
      const companyQuery = query(
        collection(db, 'notifications'),
        where('companyId', '==', currentCompanyId),
        where('audience', 'in', audiences),
        where('read', '==', false),
      )

      unsubscribeCompanyNotifications = onSnapshot(
        companyQuery,
        (companySnapshot) => {
          // Re-get personal count to ensure we have the latest
          getDocs(personalQuery)
            .then((personalSnapshot) => {
              unreadCount = personalSnapshot.size + companySnapshot.size
              isPersonalLoaded = true
              updateTotalCounts()
            })
            .catch((err) => {
              setError(err as Error)
              setIsLoading(false)
            })
        },
        (err) => {
          setError(err as Error)
          setIsLoading(false)
        },
      )
    }

    // Cleanup function
    return () => {
      if (unsubscribeCompany) {
        unsubscribeCompany()
      }
      if (unsubscribePersonal) {
        unsubscribePersonal()
      }
      if (unsubscribeCompanyNotifications) {
        unsubscribeCompanyNotifications()
      }
    }
  }, [userId, currentCompanyId, platform])

  return {
    data: counts,
    isLoading,
    error,
    refetch: () => {
      // Force re-subscription by clearing and reloading
      setCounts({ total: 0, unread: 0, followRequests: 0 })
      setIsLoading(true)
      setError(null)
    },
  }
}

export const useNotificationsRealtime = (userId: string, platform: 'web' | 'mobile' = 'web') => {
  const { userData } = useAuth()
  const companyId = userData?.companyId
  const queryClient = useQueryClient()
  const [notifications, setNotifications] = useState<SimplifiedNotification[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Prevent excessive re-renders by memoizing stable values
  const stableUserId = useMemo(() => userId, [userId])
  const stablePlatform = useMemo(() => platform, [platform])
  const stableCompanyId = useMemo(() => companyId, [companyId])

  // Stable cache update functions
  const updateListCache = useCallback(
    (notifications: SimplifiedNotification[]) => {
      queryClient.setQueryData(notificationKeys.list(stableUserId, stablePlatform, stableCompanyId), notifications)
    },
    [queryClient, stableUserId, stablePlatform, stableCompanyId],
  )

  const updateCountCache = useCallback(
    (count: number) => {
      queryClient.setQueryData(notificationKeys.count(stableUserId, stablePlatform, stableCompanyId), count)
    },
    [queryClient, stableUserId, stablePlatform, stableCompanyId],
  )

  useEffect(() => {
    if (!stableUserId) {
      setNotifications([])
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)

    const audiences = stablePlatform === 'web' ? ['web', 'all'] : ['mobile', 'all']

    // Personal notifications query
    const personalQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', stableUserId),
      where('audience', 'in', audiences),
      orderBy('createdAt', 'desc'),
      limit(50),
    )

    let unsubscribeCompany: (() => void) | null = null

    const unsubscribePersonal = onSnapshot(
      personalQuery,
      (personalSnapshot) => {
        try {
          const personalNotifs = personalSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as SimplifiedNotification[]

          // Filter out notifications with invalid timestamps
          const validNotifs = personalNotifs.filter((n) => {
            if (!n.createdAt) {
              return false
            }

            // Check if it's a valid Firestore timestamp
            if (
              typeof n.createdAt === 'object' &&
              (typeof n.createdAt.toDate === 'function' || typeof n.createdAt.seconds === 'number')
            ) {
              return true
            }

            return false
          })

          // Use valid notifications instead of original
          const filteredPersonalNotifs = validNotifs

          // If user has a company, also subscribe to company notifications
          if (stableCompanyId) {
            const companyQuery = query(
              collection(db, 'notifications'),
              where('companyId', '==', stableCompanyId),
              where('audience', 'in', audiences),
              orderBy('createdAt', 'desc'),
              limit(25),
            )

            // Clean up previous company subscription if exists
            if (unsubscribeCompany) {
              unsubscribeCompany()
            }

            unsubscribeCompany = onSnapshot(
              companyQuery,
              (companySnapshot) => {
                const companyNotifs = companySnapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                })) as SimplifiedNotification[]

                // Merge and deduplicate
                const notificationMap = new Map<string, SimplifiedNotification>()
                for (const notif of filteredPersonalNotifs) {
                  notificationMap.set(notif.id, notif)
                }
                for (const notif of companyNotifs) {
                  if (!notificationMap.has(notif.id)) {
                    notificationMap.set(notif.id, notif)
                  }
                }

                // Helper function to extract timestamp
                const getTimestamp = (createdAt: any): number => {
                  if (!createdAt) return 0

                  if (createdAt.toDate) {
                    return createdAt.toDate().getTime()
                  }

                  if (createdAt.seconds) {
                    return createdAt.seconds * 1000
                  }

                  return 0
                }

                // Sort by createdAt and limit
                const mergedNotifs = Array.from(notificationMap.values())
                  .sort((a, b) => {
                    const aTime = getTimestamp(a.createdAt)
                    const bTime = getTimestamp(b.createdAt)
                    return bTime - aTime
                  })
                  .slice(0, 50)

                setNotifications(mergedNotifs)
                setIsLoading(false)
                setError(null)

                // Update React Query cache
                updateListCache(mergedNotifs)

                // Update unread count cache
                const unreadCount = mergedNotifs.filter((n) => !n.read).length
                updateCountCache(unreadCount)
              },
              (err) => {
                setError(err as Error)
              },
            )
          } else {
            // No company - just use filtered personal notifications
            setNotifications(filteredPersonalNotifs)
            setIsLoading(false)
            setError(null)

            // Update React Query cache
            updateListCache(filteredPersonalNotifs)

            // Update unread count cache
            const unreadCount = filteredPersonalNotifs.filter((n) => !n.read).length
            updateCountCache(unreadCount)
          }
        } catch (err) {
          setError(err as Error)
          setIsLoading(false)
        }
      },
      (err) => {
        setError(err as Error)
        setIsLoading(false)
      },
    )

    // Cleanup function
    return () => {
      unsubscribePersonal()
      if (unsubscribeCompany) {
        unsubscribeCompany()
      }
    }
  }, [stableUserId, stableCompanyId, stablePlatform, updateListCache, updateCountCache]) // Use stable values to prevent infinite re-renders

  // Expose a function to update notifications directly (for optimistic updates)
  const updateNotification = (notificationId: string, updates: Partial<SimplifiedNotification>) => {
    setNotifications((current) =>
      current.map((notif) => (notif.id === notificationId ? { ...notif, ...updates } : notif)),
    )
  }

  const markAllAsReadLocal = () => {
    setNotifications((current) => current.map((notif) => ({ ...notif, read: true })))
  }

  return {
    data: notifications,
    isLoading,
    error,
    updateNotification,
    markAllAsReadLocal,
    refetch: () => {
      // Force re-subscription by clearing and reloading
      setNotifications([])
      setIsLoading(true)
      setError(null)
    },
    forceSync: () => {
      // Force complete re-synchronization
      setNotifications([])
      setIsLoading(true)
      setError(null)
      // Trigger a re-render by updating the effect dependencies
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  }
}
